import React, { memo, useCallback, useContext, useEffect, useRef } from 'react';
import { Empty } from 'antd';
import styles from './style.less';
import { SKUWrapper } from '@/models/sku';
import SpuItem from './spuItem';
import { POSConfigContext } from '@/components/LayoutPOS';
import { EmptyProps } from 'antd/es/empty';
import { FixedSizeGrid as Grid } from 'react-window';

type Props = {
  scrollTopFlag?: unknown;
  previewMode?: boolean;
  skuWrapperList: SKUWrapper[];
  onItemClick: (skuWrapper: SKUWrapper) => void;
  emptyProps?: EmptyProps;
  containerRef: React.MutableRefObject<HTMLDivElement>;
};

const SpuList: React.FC<Props> = ({
  scrollTopFlag,
  skuWrapperList,
  onItemClick,
  previewMode = false,
  emptyProps = {},
  containerRef,
}: Props) => {
  const {
    config: { uiConfig },
  } = useContext(POSConfigContext);
  const gridRef = useRef<{ scrollTo: (pos: { scrollLeft: number; scrollTop: number }) => void }>();
  let width = containerRef.current.offsetWidth;
  const height = containerRef.current.offsetHeight;

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollTo({ scrollLeft: 0, scrollTop: 0 });
    }
  }, [scrollTopFlag]);

  const renderItem = useCallback(({ columnIndex, rowIndex, style, isScrolling, data }) => {
    const index = rowIndex * data.columnCount + columnIndex;
    if (index >= data.skuWrapperList.length) {
      return null;
    }

    return (
      <div style={style}>
        <SpuItem
          loading={isScrolling}
          titleRows={data.titleRows}
          imgHeight={data.imgHeight}
          imgMode={data.imgMode}
          previewMode={data.previewMode}
          skuWrapper={data.skuWrapperList[index]}
          onItemClick={data.onItemClick}
          columnIndex={columnIndex}
        />
      </div>
    );
  }, []);

  const renderItemKey = useCallback(({ columnIndex, data, rowIndex }) => {
    const index = rowIndex * data.columnCount + columnIndex;
    if (index >= data.skuWrapperList.length) {
      return `${columnIndex}-${rowIndex}`;
    }

    const item = data.skuWrapperList[index];
    return `${item.goods.id}`;
  }, []);

  if (!skuWrapperList || !skuWrapperList.length) {
    return <Empty description={'该品类下无商品'} style={{ marginTop: 30 }} {...emptyProps} />;
  }

  let titleRows = uiConfig.coGoodsNameLines;
  // 行数 * 行高 + paddingTop + paddingBottom
  let titleHeight = uiConfig.coGoodsNameLines * 22 + 8 + 7;
  let imgHeight = uiConfig.coGoodsShowImg ? uiConfig.coGoodsImgHeight || 130 : 0;
  let footerHeight = 38;

  // 标题 + 图片 + footer + 垂直边框 + 距离
  let itemHeight = titleHeight + imgHeight + footerHeight + 4 + 10;

  let imgMode = uiConfig.coGoodsImgMode;
  const columnCount = uiConfig.coGoodsItemsRow;

  return (
    <div className={styles.skuContainer}>
      <Grid
        className={styles.spuGrid}
        ref={gridRef}
        columnCount={columnCount}
        rowCount={Math.ceil(skuWrapperList.length / uiConfig.coGoodsItemsRow)}
        columnWidth={width / uiConfig.coGoodsItemsRow}
        rowHeight={itemHeight}
        height={height}
        itemData={{
          columnCount,
          skuWrapperList: skuWrapperList,
          titleRows: titleRows,
          imgHeight: imgHeight,
          imgMode: imgMode,
          previewMode: previewMode,
          onItemClick: onItemClick,
        }}
        itemKey={renderItemKey}
        width={width}>
        {renderItem}
      </Grid>
    </div>
  );
};

export default memo(SpuList);
